import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Phone } from "lucide-react";
import { CONTACT, APPS } from "@/lib/content";
import { scrollToSection } from "@/components/SmoothScroll";
import { Logo } from "@/components/Brand";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", section: "services" },
  { label: "Advisory", to: "/business-advisory" },
  { label: "About", section: "about" },
  { label: "Contact", section: "contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const goSection = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (pathname !== "/") {
      navigate("/", { state: { anchor: id } });
    } else {
      scrollToSection(`#${id}`);
    }
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-300 ${
          scrolled || open
            ? "border-[#102036]/10 bg-[#FFFFFF]/85 backdrop-blur-xl"
            : "border-transparent bg-[#FFFFFF]/40 backdrop-blur-sm"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-[1320px] items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="header-logo-link" aria-label="Ford & Associates — home" className="shrink-0">
            <Logo />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            {NAV.map((item) =>
              item.to ? (
                <Link
                  key={item.label}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className="link-sweep text-[13px] font-semibold uppercase tracking-[0.16em] text-[#102036]/80 transition-colors hover:text-[#102036]"
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.label}
                  href={`#${item.section}`}
                  onClick={goSection(item.section)}
                  data-testid={`nav-${item.section}`}
                  className="link-sweep text-[13px] font-semibold uppercase tracking-[0.16em] text-[#102036]/80 transition-colors hover:text-[#102036]"
                >
                  {item.label}
                </a>
              ),
            )}
            <Link
              to="/client-centre"
              data-testid="nav-client-centre-button"
              className="group inline-flex items-center gap-2 rounded-full bg-[#102036] px-6 py-2.5 text-[13px] font-bold uppercase tracking-[0.14em] text-[#FFFFFF] shadow-[0_8px_24px_rgb(16,32,54,0.28)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#102036]"
            >
              Client Centre
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </nav>

          <button
            className="flex h-11 w-11 items-center justify-center rounded-md border border-[#102036]/15 bg-white/70 text-[#102036] lg:hidden"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col bg-[#102036] px-6 pt-28 pb-10 lg:hidden"
          >
            <nav className="flex flex-col gap-2" aria-label="Mobile">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.4 }}
                >
                  {item.to ? (
                    <Link
                      to={item.to}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                      className="block border-b border-[#FFFFFF]/10 py-4 font-display text-4xl text-[#FFFFFF]"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <a
                      href={`#${item.section}`}
                      onClick={goSection(item.section)}
                      data-testid={`mobile-nav-${item.section}`}
                      className="block border-b border-[#FFFFFF]/10 py-4 font-display text-4xl text-[#FFFFFF]"
                    >
                      {item.label}
                    </a>
                  )}
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.34, duration: 0.4 }}>
                <Link
                  to="/client-centre"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-client-centre"
                  className="block border-b border-[#FFFFFF]/10 py-4 font-display text-4xl text-[#FFFFFF]"
                >
                  Client Centre
                </Link>
              </motion.div>
            </nav>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.4 }}
              className="mt-auto space-y-3"
            >
              {[APPS.clientManager, APPS.taxManager].map((app) => (
                <a
                  key={app.name}
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`mobile-app-${app.name.toLowerCase().replace(" ", "-")}`}
                  className="flex items-center justify-between rounded-lg border border-[#FFFFFF]/15 px-5 py-4 text-[#FFFFFF] transition-colors hover:bg-[#FFFFFF]/10"
                >
                  <span>
                    <span className="block text-sm font-bold uppercase tracking-[0.14em]">{app.name}</span>
                    <span className="block text-xs text-[#FFFFFF]/60">{app.tagline}</span>
                  </span>
                  <ArrowUpRight className="h-5 w-5" />
                </a>
              ))}
              <a href={CONTACT.phoneHref} data-testid="mobile-menu-call" className="flex items-center gap-3 px-1 pt-2 text-[#FFFFFF]/80">
                <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
